import { PublicNav } from "@/components/PublicNav";
import { SEO } from "@/components/SEO";
import { ArrowLeft, Calendar, Clock, RefreshCw, Tag, User } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { Language } from "@/types/i18n";
import { getLocaleString } from "@/utils/dateLocalization";
import { sanitizeArticleHtml } from "@/utils/sanitizeArticleHtml";

const API_BASE = import.meta.env.VITE_API_URL || "/api";

// The same note the server-rendered page carries, from
// backend/src/services/marketing/blogLocale.ts; change both together.
const DISCLAIMER: Record<string, string> = {
  en: "Travel rules change. HealthPass does not update blog posts after they are published, so please check the current requirements before you travel.",
  de: "Reisebestimmungen können sich ändern. HealthPass aktualisiert Blogbeiträge nach der Veröffentlichung nicht. Bitte prüfen Sie die aktuellen Bestimmungen vor Ihrer Reise.",
  fr: "Les règles de voyage peuvent changer. HealthPass ne met pas à jour ses articles de blog après leur publication : vérifiez les exigences en vigueur avant votre départ.",
  es: "Las normas de viaje cambian. HealthPass no actualiza los artículos del blog después de publicarlos, así que comprueba los requisitos vigentes antes de viajar.",
  pt: "As regras de viagem mudam. A HealthPass não atualiza os artigos do blog depois de publicados, por isso confira os requisitos em vigor antes de viajar.",
};

interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  author: string;
  category: string;
  tags: string[];
  image_url: string | null;
  published_at: string;
  read_time_min: number;
  meta_title: string | null;
  meta_desc: string | null;
  keywords: string[];
  language: string;
  primary_keyword: string | null;
}

export function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    fetch(`${API_BASE}/blog/${slug}`)
      .then((r) => r.json())
      .then((json) => {
        if (json.success) setPost(json.data);
        else setError("Post not found.");
      })
      .catch(() => setError("Could not load the post."))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <RefreshCw className="h-8 w-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center gap-4">
        <p className="text-gray-500 text-lg">{error || "Post not found."}</p>
        <Link
          to="/blog"
          className="text-blue-600 hover:underline flex items-center gap-1"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Blog
        </Link>
      </div>
    );
  }

  // The date follows the post's own language, like its text.
  const publishDate = new Date(post.published_at).toLocaleDateString(
    getLocaleString(post.language as Language),
    {
      year: "numeric",
      month: "long",
      day: "numeric",
    },
  );

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={post.meta_title || `${post.title} | HealthPass`}
        description={post.meta_desc || post.excerpt}
        keywords={post.keywords?.join(", ") || ""}
        url={`https://travelhealthpass.app/blog/${post.slug}`}
        canonical={`https://travelhealthpass.app/blog/${post.slug}`}
      />

      <PublicNav />

      {/* Article */}
      <main className="pt-24 pb-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="mb-6 text-sm text-gray-500 flex items-center gap-2">
            <Link to="/" className="hover:text-blue-600">
              Home
            </Link>
            <span>/</span>
            <Link to="/blog" className="hover:text-blue-600">
              Blog
            </Link>
            <span>/</span>
            <span className="text-gray-700">{post.category}</span>
          </nav>

          {/* Category Badge */}
          <span className="bg-blue-100 text-blue-600 text-xs font-semibold px-3 py-1 rounded-full">
            {post.category}
          </span>

          {/* Title */}
          <h1 className="mt-4 text-4xl font-bold text-gray-900 leading-tight">
            {post.title}
          </h1>

          {/* Meta Row */}
          <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-gray-500 border-b border-gray-200 pb-6">
            <span className="flex items-center gap-1.5">
              <User className="h-4 w-4" />
              {post.author}
            </span>
            <span className="flex items-center gap-1.5">
              <Calendar className="h-4 w-4" />
              {publishDate}
            </span>
            <span className="flex items-center gap-1.5">
              <Clock className="h-4 w-4" />
              {post.read_time_min} min read
            </span>
          </div>

          <p className="mt-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            {DISCLAIMER[post.language] ?? DISCLAIMER.en}
          </p>

          {/* Hero Image */}
          {post.image_url && (
            <img
              src={post.image_url}
              alt={post.title}
              className="w-full rounded-xl my-8 object-cover aspect-video"
              loading="eager"
            />
          )}

          {/* Content */}
          <article
            className="prose prose-lg prose-blue max-w-none mt-8
                       prose-h2:text-2xl prose-h2:font-bold prose-h2:text-gray-900 prose-h2:mt-10 prose-h2:mb-4
                       prose-h3:text-xl prose-h3:font-semibold prose-h3:text-gray-800
                       prose-p:text-gray-700 prose-p:leading-relaxed
                       prose-li:text-gray-700 prose-li:leading-relaxed
                       prose-strong:text-gray-900
                       prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline
                       prose-blockquote:border-blue-400 prose-blockquote:bg-blue-50 prose-blockquote:rounded-r-lg prose-blockquote:py-1"
            // Model-written HTML on the app's own origin: see sanitizeArticleHtml.
            dangerouslySetInnerHTML={{
              __html: sanitizeArticleHtml(post.content),
            }}
          />

          {/* Tags */}
          {post.tags?.length > 0 && (
            <div className="mt-10 flex flex-wrap gap-2 pt-6 border-t border-gray-200">
              <Tag className="h-4 w-4 text-gray-400 mt-0.5" />
              {post.tags.map((tag) => (
                <span
                  key={tag}
                  className="bg-gray-100 text-gray-600 text-xs px-3 py-1 rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* CTA Box */}
          <div className="mt-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white text-center">
            <h2 className="text-2xl font-bold mb-3">
              Travel Safely with HealthPass
            </h2>
            <p className="text-blue-100 mb-6">
              Emergency QR code, multilingual health records, and medication
              tracking. Free for life.
            </p>
            <Link
              to="/traveler/register"
              className="inline-flex items-center bg-white text-blue-600 px-8 py-3 rounded-lg hover:bg-gray-100 transition font-semibold"
            >
              Start Free – No Credit Card
            </Link>
          </div>

          {/* Back to Blog */}
          <div className="mt-10 text-center">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-gray-500 hover:text-blue-600 transition text-sm"
            >
              <ArrowLeft className="h-4 w-4" /> Back to all articles
            </Link>
          </div>
        </div>
      </main>

      {/* Schema.org BlogPosting */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": `https://travelhealthpass.app/blog/${post.slug}`,
          },
          headline: post.title,
          description: post.excerpt,
          datePublished: post.published_at,
          dateModified: post.published_at,
          author: {
            "@type": "Organization",
            name: post.author,
            url: "https://travelhealthpass.app",
          },
          publisher: {
            "@type": "Organization",
            name: "HealthPass",
            url: "https://travelhealthpass.app",
            logo: {
              "@type": "ImageObject",
              url: "https://travelhealthpass.app/images/healthpass_logo.png",
            },
          },
          keywords: post.keywords?.join(", "),
          articleSection: post.category,
          ...(post.image_url
            ? { image: { "@type": "ImageObject", url: post.image_url } }
            : {}),
        })}
      </script>
    </div>
  );
}
